import React, { useEffect, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Label,
  Button,
} from 'reactstrap';
import { ClaimSearchDataType } from '../../utils/Types';
import '../../assets/style/Modal.css';
import { checkNullValue } from '../../utils/Util';
import { useAppDispatch } from '../../store/redux-hooks';
import {
  claimSearchSelector,
  fetchClaimDetails,
} from '../../features/claimSearchSlice';
import { useSelector } from 'react-redux';
import { useLoader } from '../../loader/LoaderProvider';
import CenterSpinner from '../../components/CenterSpinner';

interface ClaimSearchModalProps {
  isOpen: boolean;
  toggle: () => void;
  selectedClaim?: ClaimSearchDataType | null;
}

const ClaimSearchModal: React.FC<ClaimSearchModalProps> = ({
  isOpen,
  toggle,
  selectedClaim,
}) => {
  const dispatch = useAppDispatch();
  const claimID = selectedClaim?.claimNo;
  const { showLoader, setShowLoader } = useLoader();
  const claimDetails = useSelector(claimSearchSelector)?.claimDetails?.result;

  const hasFetched = useRef(false);

  useEffect(() => {
    if (claimID && !hasFetched.current) {
      hasFetched.current = true;
      setShowLoader(true);
      dispatch(fetchClaimDetails({ claimID }))
        .unwrap()
        .finally(() => {
          setShowLoader(false);
        });
    }
  }, [claimID]);

  if (!selectedClaim) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" backdrop="static">
      <div className="bg-white p-3 rounded border">
        <ModalHeader toggle={toggle}>View Claim Details</ModalHeader>
        <ModalBody>
          {/* Claim Details Section */}
          <h5 className="section-title">Claim Details</h5>
          <Row className="mb-3">
            <Col xs={12} md={3}>
              <Label className="model-label-name">Claim ID</Label>
              <br />
              <Label className="model-label-value">
                {checkNullValue(claimDetails?.claimNo ?? 'N/A')}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Farmer ID</Label>
              <br />
              <Label className="model-label-value">
                {checkNullValue(selectedClaim?.policy?.custemerID ?? 'N/A')}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Policy ID</Label>
              <br />
              <Label className="model-label-value">
                {checkNullValue(selectedClaim?.policy?.policyNumber ?? 'N/A')}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Farmer Name</Label>
              <br />
              <Label className="model-label-value">
                {checkNullValue(selectedClaim?.policy?.custmerName ?? 'N/A')}
              </Label>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={12} md={3}>
              <Label className="model-label-name">State</Label>
              <br />
              <Label className="model-label-value">
                {selectedClaim?.policy?.state ?? 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">District</Label>
              <br />
              <Label className="model-label-value">
                {selectedClaim?.policy?.district ?? 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Mandal</Label>
              <br />
              <Label className="model-label-value">
                {selectedClaim?.policy?.mandal ?? 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Village</Label>
              <br />
              <Label className="model-label-value">
                {selectedClaim?.policy?.village ?? 'N/A'}
              </Label>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={12} md={3}>
              <Label className="model-label-name">Crop Name</Label>
              <br />
              <Label className="model-label-value">
                {claimDetails?.riskName ?? 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Season</Label>
              <br />
              <Label className="model-label-value">
                {selectedClaim?.policy?.seasonName || 'NA'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Year</Label>
              <br />
              <Label className="model-label-value">
                {claimDetails?.claimOpenDate
                  ? new Date(claimDetails.claimOpenDate)
                      .getFullYear()
                      .toString()
                  : 'NA'}
              </Label>
            </Col>
          </Row>

          {/* Claim Status Section */}
          <h5 className="section-title mt-4">Claim Status</h5>
          <Row className="mb-3">
            <Col xs={12} md={3}>
              <Label className="model-label-name">Status</Label>
              <br />
              <Label style={{ color: '#DFA624' }}>
                {claimDetails?.claimStatus ?? 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Sub Status</Label>
              <br />
              <Label style={{ color: '#DFA624' }}>
                {claimDetails?.claimSubstatus ?? 'N/A'}
              </Label>
            </Col>
          </Row>

          {/* Event Details Section */}
          <h5 className="section-title mt-4">Event Details</h5>
          <Row className="mb-3">
            <Col xs={12} md={3}>
              <Label className="model-label-name">Event Occur Type</Label>
              <br />
              <Label className="model-label-value">
                {claimDetails?.perilName ?? 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Event Occur Date</Label>
              <br />

              <Label className="model-label-value">
                {claimDetails?.dateAndTimeOfLoss
                  ? new Date(claimDetails.dateAndTimeOfLoss)
                      .toISOString()
                      .split('T')[0]
                  : 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Intimation Date</Label>
              <br />

              <Label className="model-label-value">
                {claimDetails?.claimOpenDate
                  ? new Date(claimDetails.claimOpenDate)
                      .toISOString()
                      .split('T')[0]
                  : 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Intimation Source</Label>
              <br />
              <Label className="model-label-value">
                {claimDetails?.claimIntimatedBy ?? 'N/A'}
              </Label>
            </Col>
          </Row>

          {/* Payment Info Section */}
          <h5 className="section-title mt-4">Payment Info</h5>
          <Row className="mb-3">
            <Col xs={12} md={3}>
              <Label className="model-label-name">Claim Amount</Label>
              <br />
              <Label className="model-label-value">
                {claimDetails?.estimatedAmount?.toString() ?? 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">Reason for Rejection</Label>
              <br />
              <Label className="model-label-value">
                {claimDetails?.claimRemarks ?? 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">UTR Details</Label>
              <br />
              <Label className="model-label-value">
                {claimDetails?.paymentDetails?.disbursementUTRNum ?? 'N/A'}
              </Label>
            </Col>
            <Col xs={12} md={3}>
              <Label className="model-label-name">UTR Date</Label>
              <br />

              <Label className="model-label-value">
                {claimDetails?.paymentDetails?.disbursementDateTime
                  ? new Date(
                      claimDetails.paymentDetails.disbursementDateTime
                    ).toLocaleDateString('en-GB')
                  : 'N/A'}
              </Label>
            </Col>
          </Row>

          {/* Close Button */}
          <div className="mt-4 d-flex justify-content-center">
            <Button
              variant="primary"
              className="cl-btn-primary"
              onClick={toggle}
            >
              Close
            </Button>
          </div>
          <CenterSpinner showLoader={showLoader} className="spinner-60" />
        </ModalBody>
      </div>
    </Modal>
  );
};

export default ClaimSearchModal;
